import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, getApiErrorMessage } from '@/shared/api/axios';

export type ActivityMetric = 'calls_made' | 'calls_connected' | 'meetings_held' | 'followups_done';
export type ActivityPeriod = 'day' | 'week' | 'month';

export interface ActivityTargetRow {
  metric: ActivityMetric;
  period: ActivityPeriod;
  value: number;
  /** Only on a person's own row: the metric does not apply to them. */
  exempt?: boolean;
}

export interface EffectiveActivityTarget {
  metric: ActivityMetric;
  period: ActivityPeriod;
  value: number;
  source: 'default' | 'person';
}

export interface ActivityTargetsSetup {
  metrics: Array<{ metric: ActivityMetric; label: string; hint: string }>;
  periods: Array<{ period: ActivityPeriod; label: string }>;
  /** Departments that get the company default (Settings > HR > Performance & Incentives). */
  salesDepartments: string[];
  defaults: ActivityTargetRow[];
  people: Array<{
    id: string;
    name: string;
    department: string;
    departmentRole: string;
    /** The person's own rows (a target of their own, or exempt). */
    overrides: ActivityTargetRow[];
    effective: EffectiveActivityTarget[];
  }>;
}

const KEY = ['hr', 'activity-targets'] as const;

export function useActivityTargetsSetup() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get<ActivityTargetsSetup>('/activity-targets')).data,
    staleTime: 30 * 1000,
  });
}

export function useSaveActivityDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (targets: Array<{ metric: ActivityMetric; period: ActivityPeriod; value: number }>) =>
      (await api.put('/activity-targets/defaults', { targets })).data,
    onSuccess: () => {
      toast.success('Company activity targets saved.');
      queryClient.invalidateQueries({ queryKey: KEY });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'widget'] });
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Could not save the targets.')),
  });
}

export function useSavePersonTargets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, overrides }: { userId: string; overrides: ActivityTargetRow[] }) =>
      (await api.put(`/activity-targets/users/${userId}`, { overrides })).data,
    onSuccess: () => {
      toast.success('Targets saved.');
      queryClient.invalidateQueries({ queryKey: KEY });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'widget'] });
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Could not save the targets.')),
  });
}
